import { AlertTriangle, Info, ShieldCheck } from "lucide-react"

import { cn } from "@/lib/utils"
import type { Advertencia } from "@/routes/holding/api"
import { t } from "@/lib/i18n"

/**
 * LO QUE EL CONSOLIDADO NO PUDO HACER, ANTES DEL NUMERO.
 *
 * Es el componente que define esta seccion, y no es decoracion. Consolidar
 * obliga a suponer cosas —el perimetro, el porcentaje de participacion, el tipo
 * de cambio promedio cuando no hay cotizaciones— y un supuesto que quien firma
 * no ve es una afirmacion falsa. Un consolidado que cuadra y que miente es peor
 * que uno que dice que le falta.
 *
 * Por eso va ARRIBA del estado y no en un pie: cuando esta abajo, se lee
 * despues de haber creido el numero.
 *
 * Dos niveles, y la diferencia es si cambia la DECISION:
 *
 *   grave   el numero que estas mirando no significa lo que parece. Perimetro
 *           supuesto, una sociedad que no aporta, un balance que no cuadra.
 *   aviso   el numero es correcto y su precision es menor de lo ideal.
 *
 * Sin advertencias NO se calla: dice que no las hay. La ausencia de alarma es
 * informacion, y un hueco en blanco no se distingue de un componente roto.
 *
 * SE AGRUPAN, NO SE APILAN. Cuatro bloques rojos seguidos —uno por advertencia—
 * es exactamente lo que el brandbook prohibe: «las alertas informan; no
 * gritan». Y tienen el efecto contrario al que buscan, porque a la tercera ya
 * nadie las lee. Van en UN bloque con el titular una sola vez y las
 * advertencias como lista: misma informacion, un solo golpe de atencion.
 *
 * `de` nombra el sujeto cuando hay varios en la pantalla. Sin eso, «esta
 * sociedad no declara su regimen» aparece encima de una tabla y obliga a
 * adivinar de cual habla.
 */
export function Advertencias({
  advertencias,
  de,
  className,
}: {
  advertencias: Advertencia[]
  /** De quién son. Se muestra cuando la pantalla tiene varios sujetos. */
  de?: string | null
  className?: string
}) {
  const graves = advertencias.filter((a) => a.grave)
  const avisos = advertencias.filter((a) => !a.grave)

  if (advertencias.length === 0) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border border-success/25 bg-success-soft px-3 py-2",
          className,
        )}
      >
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-success" />
        <p className="text-xs text-success">
          {t("Marty no tuvo que suponer nada: el perímetro está declarado, hay cotización para cada moneda y las dos patas de cada operación entre sociedades coinciden.")}</p>
      </div>
    )
  }

  return (
    <div className={cn("space-y-1.5", className)} role="status">
      {graves.length > 0 ? (
        <div className="rounded-md border border-destructive/35 bg-destructive-soft px-3 py-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-destructive" />
            <p className="text-2xs font-medium uppercase tracking-[0.06em] text-destructive">
              {graves.length === 1
                ? "Cambia lo que significa el número"
                : `${graves.length} cosas cambian lo que significa el número`}
              {de ? <span className="normal-case tracking-normal"> · {de}</span> : null}
            </p>
          </div>
          {/* Una sola advertencia no se pinta como lista: una viñeta suelta
              parece el principio de algo que falta. */}
          {graves.length === 1 ? (
            <p className="ml-[1.375rem] mt-0.5 text-xs leading-[1.2rem] text-foreground/85">
              {graves[0].texto}
            </p>
          ) : (
            <ul className="ml-[1.375rem] mt-1 space-y-1">
              {graves.map((a) => (
                <li
                  key={a.clave}
                  className="list-disc text-xs leading-[1.2rem] text-foreground/85 marker:text-destructive"
                >
                  {a.texto}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      {avisos.length > 0 ? (
        <div className="rounded-md border bg-surface-sunken px-3 py-2">
          <div className="flex items-start gap-2">
            <Info className="mt-px h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0 space-y-1">
              {avisos.map((a) => (
                <p key={a.clave} className="text-xs leading-[1.2rem] text-muted-foreground">
                  {a.texto}
                </p>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
